import {useCallback, useMemo, useState} from 'react'
import {ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import {ButtonGroup, Button, IconButton} from '@primer/react'
import {LockIcon, RepoIcon, RepoLockedIcon, TriangleDownIcon} from '@primer/octicons-react'
import type {ContextCrumb} from '../../types'
import styles from './styles.module.css'
import {useRepositorySuggestions, type Repository} from './use-repository-suggestions'

export interface RepoPickerProps {
  repositoryCrumb: ContextCrumb
  ownerCrumb: ContextCrumb
}

export function RepoPicker({repositoryCrumb, ownerCrumb}: RepoPickerProps) {
  const [isOpen, setIsOpen] = useState(false)

  const {repositories, isLoading, searchQuery, handleFilterChange} = useRepositorySuggestions({
    owner: ownerCrumb.label,
    enabled: isOpen,
    currentRepository: repositoryCrumb.label,
  })

  const convertToItemProps = useCallback((repo: Repository) => {
    const isPrivate = repo.visibility === 'private' || repo.visibility === 'internal'

    return {
      id: repo.nameWithOwner,
      text: repo.name,
      source: repo,
      leadingVisual: () => (isPrivate ? <RepoLockedIcon size={16} /> : <RepoIcon size={16} />),
    }
  }, [])

  const getItemKey = useCallback((repo: Repository) => repo.nameWithOwner, [])

  const handleSelectionChange = useCallback((items: Repository[]) => {
    const selectedRepo = items[0]
    if (selectedRepo) {
      window.location.href = `/${selectedRepo.nameWithOwner}`
    }
  }, [])

  const currentRepoHref = repositoryCrumb.href || repositoryCrumb.path || `/${repositoryCrumb.label}`
  const isCurrentRepoPrivate = repositoryCrumb.octicon === 'lock'
  const currentRepoName = repositoryCrumb.label.split('/').pop() || repositoryCrumb.label

  const initialSelectedItems = useMemo(() => {
    const currentRepo = repositories.find(repo => repo.name === repositoryCrumb.label)
    return currentRepo ? [currentRepo] : []
  }, [repositories, repositoryCrumb.label])

  return (
    <ItemPicker<Repository>
      renderAnchor={({'aria-labelledby': ariaLabelledBy, 'aria-label': ariaLabel, ...anchorProps}) => (
        <ButtonGroup className={styles.repoPickerButtonGroup} data-menu-open={isOpen}>
          <Button
            as="a"
            href={currentRepoHref}
            leadingVisual={isCurrentRepoPrivate ? LockIcon : undefined}
            variant="invisible"
            size="small"
            className={styles.repoPickerCrumb}
          >
            {currentRepoName}
          </Button>
          <IconButton
            {...anchorProps}
            icon={TriangleDownIcon}
            variant="invisible"
            size="small"
            aria-label="Switch repository"
            className={styles.repoPickerDropdownButton}
          />
        </ButtonGroup>
      )}
      items={repositories}
      initialSelectedItems={initialSelectedItems}
      placeholderText="Search repositories"
      selectionVariant="single"
      loading={isLoading}
      filterItems={handleFilterChange}
      getItemKey={getItemKey}
      convertToItemProps={convertToItemProps}
      onSelectionChange={handleSelectionChange}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      title="Switch repository"
      width="medium"
      improvedNoMatchAccessibility
      noMatchMessage={
        isLoading
          ? {variant: 'empty' as const, title: 'Loading repositories...', body: ''}
          : repositories.length === 0 && searchQuery
            ? {variant: 'empty' as const, title: 'No repositories found', body: ''}
            : undefined
      }
    />
  )
}

try{ RepoPicker.displayName ||= 'RepoPicker' } catch {}